import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { notification } from 'ant-design-vue';
import AppSidebar from '@/components/global/sidebar/AppSidebar.vue';
import { UserRepository } from '@/repositories/UserRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { RouteNames } from '@/enums/routes/RouteNames';
import AutocompleteGroupInput from '@/components/global/AutocompleteGroupInput.vue';
import ProjectRepository from '@/repositories/ProjectRepository';
import { AxiosResponse } from 'axios';
import ProjectModel from '@/models/Project';
import { ProjectTabValues } from '@/enums/components/Project/ProjectTabValues';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import ConfiguratorService from '@/services/ConfiguratorService';
import OfferItem from '@/models/OfferItem';
import { JsonAPIError } from '@/interfaces/general/JsonAPIError';
import { formatFormData } from '@/helpers/FormDataHelper';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import Montage from '@/models/Montage';
import PurchasePriceManipulation from '@/models/PurchasePriceManipulation';
import Offer from '@/models/Offer';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import ClientRepository from '@/repositories/ClientRepository';
import router from '@/router';
import i18n from '@/i18n';
import { formatNumberForDisplay } from '@/helpers/NumberFormatter';

interface Discount {
    amount: number;
    type: string;
    label: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CalculatorSidebar',
  setup(__props) {

const { proxy } = getCurrentInstance()!;

const clientsInput = ref<InstanceType<typeof AutocompleteGroupInput> | null>(null);
const projectId = ref('');
const production = ref<boolean>();
const connection = ref<number | null>();
const checklistFieldErrorsExist = ref(false);
const paymentTypeId = ref<string | null>();
const productFormValues = ref<ActiveProductFormValueObject | null>();
const productFormId = ref<number | null>();
const price = ref(0.0);
const tax = ref(0.0);
const taxValue = ref(tax.value);
const discount = ref(5);
const discounts = ref<Discount[]>([]);
const montage = ref(0.0);
const montageValue = ref(montage.value);
const salesPrice = ref(0.0);
const finalPrice = ref(0.0);
const modalVisible = ref(false);
const typeOfDiscount = ref('percentage');
const editableTax = ref(false);
const defaultClientId = ref('');
const selectedClientId = ref<string | null>(null);
const loadingOverlay = new LoadingOverlayHelper(proxy, {});
const formFieldErrorsExist = ref(false);
const eventFinishedCallback = ref<(() => void) | null>(null);
const eventFinished = ref(false);

const roundToTwo = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;

const currencySymbol = computed(() => UserRepository.getCurrentUsersCurrencySymbol());
const user = computed(() => UserRepository.getCurrentUser());
const client = computed(() => ClientRepository.getById(selectedClientId.value as string));
const isProductView = computed(() => router.currentRoute.name === RouteNames.calculatorProduct);
const formattedPrice = computed(() => formatNumberForDisplay(price.value, 2, 2));
const formattedSalesPrice = computed(() => formatNumberForDisplay(salesPrice.value, 2, 2));
const formattedFinalPrice = computed(() => formatNumberForDisplay(roundToTwo(finalPrice.value), 2, 2));
const translate = (key: string) => i18n.t(key) as string;

const updateCalculatorProductValues = ({
    productFormValues: updatedProductFormValues,
    connection: updatedConnection,
    production: updatedProduction,
    checklistFieldErrorsExist: updatedChecklistFieldErrorsExist,
    paymentTypeId: updatedPaymentTypeId,
    productFormId: updatedProductFormId,
}: any) => {
    productFormValues.value = updatedProductFormValues;
    connection.value = updatedConnection;
    production.value = updatedProduction;
    checklistFieldErrorsExist.value = updatedChecklistFieldErrorsExist;
    paymentTypeId.value = updatedPaymentTypeId;
    productFormId.value = updatedProductFormId;
};

const clearAll = () => {
    price.value = 0.0;
    salesPrice.value = 0.0;
    tax.value = 0.0;
    discounts.value = [];
    montage.value = 0.0;
    selectedClientId.value = null;
    if (router.currentRoute.name !== RouteNames.calculator) {
        router.push({
            name: RouteNames.calculator,
        });
    }
};

const calculateFinalPrice = () => {
    // (price + discount) + montage + taxes
    let discountValue;
    finalPrice.value = salesPrice.value;
    discounts.value.forEach((value) => {
        if (value.type === 'percentage') {
            discountValue = (salesPrice.value * value.amount) / 100;
            finalPrice.value = finalPrice.value - discountValue;
        }
        if (value.type === 'money') {
            finalPrice.value = finalPrice.value - value.amount;
        }
    });
    finalPrice.value = finalPrice.value + montage.value;
    finalPrice.value = finalPrice.value + (finalPrice.value * tax.value) / 100;
};

const onPriceRecalcualted = (prices: any) => {
    if (prices?.length === 0 || !prices) {
        price.value = 0;
        finalPrice.value = 0;
        salesPrice.value = 0.0;
        clientsInput.value!.clearInput();
        selectedClientId.value = null;
        defaultClientId.value = '';
    } else {
        const key = 'priceValue';
        price.value = prices.purchasePrices[prices.purchasePrices.length - 1][key];
        salesPrice.value = prices.sellingPrices[prices.sellingPrices.length - 1][key];
        calculateFinalPrice();
    }
};

const onClientSelect = (clientId: string) => {
    selectedClientId.value = clientId;

    EventBus.$emit(EventBusEvents.calculatorClientChosen, selectedClientId.value);
};

const showModal = () => {
    modalVisible.value = true;
};

const getAssignedUserId = () => {
    if (!client.value) {
        return user.value?.id ?? '';
    } else {
        return client.value.assignedUser?.id ?? user.value?.id ?? '';
    }
};

const handleSubmit = (e: any) => {
    discounts.value.push({
        amount: discount.value,
        type: typeOfDiscount.value,
        label:
            typeOfDiscount.value === 'percentage'
                ? translate('Discount') + ` ${discount.value}%`
                : translate('Discount') + ` ${discount.value} ${currencySymbol.value}`,
    });
    modalVisible.value = false;
    calculateFinalPrice();
};

const addProductToOffer = async () => {
    const currentProjectId = projectId.value as string;
    const routeOfferId = router.currentRoute.query.offerId as string;
    let validOfferId;

    try {
        validOfferId = await ConfiguratorService.getValidOfferId(
            currentProjectId,
            routeOfferId,
            paymentTypeId.value as string | null,
        );
    } catch (e) {
        notification.error({
            message: translate('Dogodila se greška'),
            description: (e as Error).message,
        });
        loadingOverlay.stop();
        return;
    }

    const formData: Array<{ id: string; value: ConfiguratorValue }> = formatFormData(
        productFormValues.value as ActiveProductFormValueObject,
    );

    try {
        // tslint:disable-next-line:max-line-length
        await OfferItem.createNew(
            formData,
            String(validOfferId),
            checklistFieldErrorsExist.value,
            connection.value as number | null,
            production.value as boolean,
            null,
            productFormId.value as number | null,
        );
    } catch (e) {
        const errorObj = e as ErrorOptions;

        errorObj.response?.data?.errors?.forEach((error: JsonAPIError) => {
            notification.error({
                message: translate('Dogodila se greška'),
                description: error.title ? error.title : errorObj.message,
            });
        });
        loadingOverlay.stop();
        return;
    }

    // Tax
    Offer.updateTaxAndExpirationDateAndConfigurationOverride(
        validOfferId,
        tax.value,
        null,
        '',
        undefined,
        paymentTypeId.value,
    );

    // Add montage
    if (montage.value !== 0) {
        await Montage.createNewAndSaveLocally(
            {
                delivery: false,
                assembly: true,
                pickup: false,
                price: montage.value,
                numberOfWorkers: 0,
                expectedTime: 0,
                electrician: false,
                deconstruction: false,
                removal: false,
            },
            validOfferId,
        );
    }

    // Discounts
    let type = '1';
    for (const currentDiscount of discounts.value) {
        if (currentDiscount.type === 'money' && currentDiscount.amount < 0) {
            type = '1';
        } else if (currentDiscount.type === 'percentage' && currentDiscount.amount < 0) {
            type = '2';
        } else if (currentDiscount.type === 'money' && currentDiscount.amount > 0) {
            type = '3';
        } else if (currentDiscount.type === 'percentage' && currentDiscount.amount > 0) {
            type = '4';
        }

        await PurchasePriceManipulation.createNew({
            productCategory: '0',
            priceManipulationType: type,
            name: '',
            value: currentDiscount.amount,
            offer: validOfferId,
        });
    }

    EventBus.$emit(EventBusEvents.changesInDataMade, { state: false });

    router.push({
        name: RouteNames.project,
        params: { id: currentProjectId },
        query: {
            offerId: validOfferId,
            initialTab: `${ProjectTabValues.Products}`,
        },
    });
    loadingOverlay.stop();
};

const createNewProject = async () => {
    loadingOverlay.start();
    eventFinishedCallback.value = null;
    EventBus.$emit(EventBusEvents.validateFormFields);

    await new Promise<void>((resolve) => {
        eventFinishedCallback.value = resolve;

        if (eventFinished.value) {
            eventFinished.value = false;
            resolve();
        }
    });

    if (formFieldErrorsExist.value) {
        loadingOverlay.stop();
        return;
    }

    const assignedUserId = getAssignedUserId();

    const response = (await ProjectModel.createNew({
        defaultClientId: selectedClientId.value,
        assignedUserId,
        madeByUserId: user.value?.id ?? '',
    })) as AxiosResponse;

    await nextTick();
    const project = ProjectRepository.getProjectById(response.data.id);

    if (!project) {
        throw new Error('There was an error while creation a new project.');
    }

    projectId.value = project.id;
    addProductToOffer();
};

const deleteDiscount = (index: number) => {
    if (index > -1) {
        discounts.value.splice(index, 1);
    }
    calculateFinalPrice();
};

const handleDiscountTypeChange = (value: any) => {
    if (typeof value !== 'string') {
        typeOfDiscount.value = value.key;
    } else {
        typeOfDiscount.value = value;
    }
};

const handleDiscount = (value: number) => {
    discount.value = value;
};

const handleCancel = (e: any) => {
    modalVisible.value = false;
};

const editTax = () => {
    editableTax.value = true;
};

const handleTax = (value: number) => {
    taxValue.value = value;
};

const handleMontage = (value: number) => {
    montageValue.value = value;
};

const cancelTax = () => {
    taxValue.value = 0.0;
    montageValue.value = 0.0;
    editableTax.value = false;
};

const saveTax = () => {
    tax.value = taxValue.value;
    montage.value = montageValue.value;
    editableTax.value = false;
    calculateFinalPrice();
};

const continueExecution = (hasFormFieldErrors: boolean) => {
    formFieldErrorsExist.value = hasFormFieldErrors;

    if (eventFinishedCallback.value) {
        eventFinishedCallback.value();
    } else {
        eventFinished.value = true;
    }

    eventFinishedCallback.value = null;
};

const onRouteChange = () => {
    onPriceRecalcualted([]);
};

watch(() => router.currentRoute, onRouteChange);

EventBus.$on(EventBusEvents.calculatorPurchasePrice, onPriceRecalcualted);
EventBus.$on(EventBusEvents.calculatorProductData, updateCalculatorProductValues);
EventBus.$on(EventBusEvents.calculatorCreateProject, createNewProject);
EventBus.$on(EventBusEvents.clearCalculator, clearAll);
EventBus.$on(EventBusEvents.formValidationFinished, continueExecution);

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.calculatorPurchasePrice, onPriceRecalcualted);
    EventBus.$off(EventBusEvents.clearCalculator, clearAll);
    EventBus.$off(EventBusEvents.calculatorProductData, updateCalculatorProductValues);
    EventBus.$off(EventBusEvents.calculatorCreateProject, createNewProject);
    EventBus.$off(EventBusEvents.formValidationFinished, continueExecution);
});

return { __sfc: true,proxy, clientsInput, projectId, production, connection, checklistFieldErrorsExist, paymentTypeId, productFormValues, productFormId, price, tax, taxValue, discount, discounts, montage, montageValue, salesPrice, finalPrice, modalVisible, typeOfDiscount, editableTax, defaultClientId, selectedClientId, loadingOverlay, formFieldErrorsExist, eventFinishedCallback, eventFinished, roundToTwo, currencySymbol, user, client, isProductView, formattedPrice, formattedSalesPrice, formattedFinalPrice, translate, updateCalculatorProductValues, clearAll, calculateFinalPrice, onPriceRecalcualted, onClientSelect, showModal, getAssignedUserId, handleSubmit, addProductToOffer, createNewProject, deleteDiscount, handleDiscountTypeChange, handleDiscount, handleCancel, editTax, handleTax, handleMontage, cancelTax, saveTax, continueExecution, onRouteChange, AppSidebar, AutocompleteGroupInput }
}

})